<template>
  <v-dialog v-model="dialog" max-width="960" :persistent="saving" @keydown.esc="close">
    <v-card class="poster-ad-modal">
      <v-card-title class="text-uppercase pb-0 d-flex align-center justify-space-between">
        <span>{{ isEdit ? 'Editar poster' : 'Nuevo poster' }}</span>
        <v-btn icon small :disabled="saving" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <hr class="mx-6 my-3" />
      <v-card-text class="pb-0">
        <v-form ref="form" v-model="formValid">
          <v-row dense>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="form.title"
                label="Título"
                outlined dense
                maxlength="255"
                :rules="[v => !!(v && v.trim()) || 'Requerido']"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.order"
                label="Orden en el carrusel"
                type="number"
                outlined dense
                :rules="[v => Number.isInteger(Number(v)) && v !== null && v !== '' || 'Debe ser un número entero']"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.subtitle" label="Subtítulo" outlined dense maxlength="255" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.description" label="Descripción" outlined dense maxlength="255" />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.internalDescription"
                label="Descripción interna"
                hint="Solo visible en el admin"
                persistent-hint
                outlined dense
                maxlength="255"
              />
            </v-col>
          </v-row>

          <v-row dense class="mt-2">
            <v-col cols="12" sm="4">
              <v-switch v-model="form.visible" label="Visible" dense hide-details class="mt-0" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-switch v-model="form.available" label="Disponible" dense hide-details class="mt-0" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-switch v-model="form.isb2b" label="B2B" dense hide-details class="mt-0" />
            </v-col>
          </v-row>

          <v-row dense class="mt-4">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.startDate"
                label="Fecha de inicio"
                type="date"
                outlined dense clearable
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.endDate"
                label="Fecha de término"
                type="date"
                hint="La app oculta el poster cuando esta fecha ya pasó"
                persistent-hint
                outlined dense clearable
                :rules="[endDateRule]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.countryId"
                :items="countries"
                item-text="text"
                item-value="id"
                label="País"
                outlined dense clearable
                no-data-text="Sin resultados"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="form.allianceId"
                :items="alliances"
                item-text="name"
                item-value="id"
                label="Alianza (opcional)"
                outlined dense clearable
                no-data-text="Sin resultados"
              />
            </v-col>
          </v-row>

          <p class="pw-label mt-2 mb-2">Redirección</p>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.webUrl" label="URL web" placeholder="https://..." outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.appRoute" label="Ruta en la app" outlined dense />
            </v-col>
          </v-row>

          <p class="pw-label mt-2 mb-2">Imágenes</p>
          <v-row dense>
            <v-col cols="12" md="5">
              <v-text-field v-model="form.deskImage" label="URL imagen escritorio" placeholder="https://..." outlined dense />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field v-model="form.mobileImage" label="URL imagen móvil" placeholder="https://..." outlined dense />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                v-model="form.backgroundColor"
                label="Color de fondo"
                placeholder="#FFFFFF"
                outlined dense
                :rules="[hexColorRule]"
              />
            </v-col>
            <v-col v-if="form.deskImage || form.mobileImage" cols="12" class="d-flex" style="gap: 12px">
              <v-img
                v-if="form.deskImage"
                :src="form.deskImage"
                max-height="90" max-width="220" contain
                class="image-preview"
                :style="previewStyle"
              />
              <v-img
                v-if="form.mobileImage"
                :src="form.mobileImage"
                max-height="90" max-width="120" contain
                class="image-preview"
                :style="previewStyle"
              />
            </v-col>
          </v-row>

          <p class="pw-label mt-4 mb-1">Visible solo para contratos</p>
          <p class="text-caption mb-3 scope-hint">
            Sin contratos, el poster lo ven todos los usuarios del país. Con contratos, solo lo ven
            los usuarios con acceso a alguno de ellos.
          </p>
          <v-row dense>
            <v-col cols="12" md="5">
              <v-autocomplete
                v-model="selectedCompanyId"
                :items="companies"
                item-text="name"
                item-value="id"
                label="Empresa"
                :loading="loadingCompanies"
                outlined dense clearable
                no-data-text="Sin resultados"
                @change="onCompanyChange"
              />
            </v-col>
            <v-col cols="12" md="7">
              <v-autocomplete
                v-model="currentCompanyContractIds"
                :items="companyContracts"
                item-text="label"
                item-value="id"
                label="Contratos de la empresa"
                :loading="loadingContracts"
                :disabled="!selectedCompanyId"
                :hint="contractsHint"
                persistent-hint
                multiple small-chips deletable-chips
                outlined dense
                no-data-text="Sin contratos"
              />
            </v-col>
          </v-row>
          <div class="mt-2">
            <v-chip
              v-for="contract in selectedContracts"
              :key="contract.id"
              small close class="mr-2 mb-2"
              @click:close="removeContract(contract.id)"
            >
              <strong v-if="contract.companyName" class="mr-1">{{ contract.companyName }} ·</strong>
              {{ contract.label }}
            </v-chip>
            <v-chip v-if="!selectedContracts.length" small outlined>Todos</v-chip>
          </div>

          <p v-if="formError" class="caption error--text mt-3 mb-0">{{ formError }}</p>
        </v-form>
      </v-card-text>
      <hr class="mx-6 my-3" />
      <v-card-actions class="justify-end px-6 pb-4">
        <v-btn text color="primary" rounded :disabled="saving" @click="close">Cancelar</v-btn>
        <v-btn
          v-if="canSubmit"
          color="primary"
          rounded
          depressed
          :loading="saving"
          :disabled="saving"
          @click="submit"
        >
          {{ isEdit ? 'Guardar cambios' : 'Crear poster' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PosterAdsService from '../services/poster-ads.service';
import CompaniesService from '../services/companie.service';
import ContratoService from '../services/contrato.service';
import {
  emptyPosterAdForm,
  toPosterAdForm,
  buildPosterAdPayload,
  toContractOption
} from '../utils/poster-ads.utils';
import { can } from '../utils/permissions';

const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export default {
  name: 'PosterAdModal',
  props: {
    value: { type: Boolean, default: false },
    // Poster-ad being edited; null creates a new one.
    posterAd: { type: Object, default: null },
    countries: { type: Array, default: () => [] },
    alliances: { type: Array, default: () => [] },
    // Map contractId -> { id, companyName, label }, used to show the contracts already assigned.
    contractIndex: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      form: emptyPosterAdForm(),
      formValid: false,
      formError: '',
      saving: false,
      companies: [],
      loadingCompanies: false,
      selectedCompanyId: null,
      companyContracts: [],
      loadingContracts: false,
      selectedContracts: []
    };
  },
  computed: {
    dialog: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); }
    },
    isEdit() {
      return !!(this.posterAd && this.posterAd.id);
    },
    canSubmit() {
      return can(this.isEdit ? 'poster-ads:update' : 'poster-ads:create');
    },
    // Ids selected among the contracts of the current company; other companies' picks are kept.
    currentCompanyContractIds: {
      get() {
        const ids = new Set(this.companyContracts.map((c) => c.id));
        return this.selectedContracts.filter((c) => ids.has(c.id)).map((c) => c.id);
      },
      set(ids) {
        const companyIds = new Set(this.companyContracts.map((c) => c.id));
        const others = this.selectedContracts.filter((c) => !companyIds.has(c.id));
        const picked = this.companyContracts.filter((c) => ids.includes(c.id));
        this.selectedContracts = [...others, ...picked];
      }
    },
    contractsHint() {
      if (!this.selectedCompanyId || this.loadingContracts) return '';
      return this.companyContracts.length ? '' : 'La empresa no tiene contratos';
    },
    previewStyle() {
      return HEX_COLOR.test(this.form.backgroundColor) ? { backgroundColor: this.form.backgroundColor } : {};
    }
  },
  watch: {
    value(open) {
      if (open) this.init();
    }
  },
  methods: {
    init() {
      this.form = this.posterAd ? toPosterAdForm(this.posterAd) : emptyPosterAdForm();
      this.selectedContracts = this.resolveContracts(this.posterAd ? this.posterAd.contratoId : null);
      this.selectedCompanyId = null;
      this.companyContracts = [];
      this.formError = '';
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation(); });
      this.loadCompanies();
    },
    resolveContracts(contractIds) {
      if (!Array.isArray(contractIds)) return [];
      return contractIds.map((id) =>
        this.contractIndex[id] || { id, companyName: '', label: `Contrato no encontrado (${id})` }
      );
    },
    async loadCompanies() {
      if (this.companies.length) return;
      this.loadingCompanies = true;
      const res = await CompaniesService.getCompaniesWithCountry();
      if (res.status === 'ok' && Array.isArray(res.data)) {
        this.companies = res.data;
      } else {
        this.formError = 'No se pudieron cargar las empresas';
      }
      this.loadingCompanies = false;
    },
    async onCompanyChange(companyId) {
      this.companyContracts = [];
      if (!companyId) return;
      this.loadingContracts = true;
      const company = this.companies.find((c) => c.id === companyId);
      const res = await ContratoService.getByCompanyId(companyId);
      if (res.status === 'ok' && Array.isArray(res.data)) {
        this.companyContracts = res.data.map((c) => toContractOption(c, company ? company.name : ''));
      } else {
        this.formError = 'No se pudieron cargar los contratos de la empresa';
      }
      this.loadingContracts = false;
    },
    removeContract(contractId) {
      this.selectedContracts = this.selectedContracts.filter((c) => c.id !== contractId);
    },
    endDateRule(value) {
      if (!value || !this.form.startDate) return true;
      return value >= this.form.startDate || 'Debe ser posterior a la fecha de inicio';
    },
    hexColorRule(value) {
      return !value || HEX_COLOR.test(value) || 'Formato #RRGGBB';
    },
    async submit() {
      this.formError = '';
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      const payload = buildPosterAdPayload(this.form, this.selectedContracts.map((c) => c.id));
      const res = this.isEdit
        ? await PosterAdsService.update(this.posterAd.id, payload)
        : await PosterAdsService.create(payload);
      this.saving = false;
      if (res.status === 'ok') {
        this.$emit('saved', this.isEdit ? 'Poster actualizado correctamente' : 'Poster creado correctamente');
        this.close();
      } else {
        this.formError = 'No se pudo guardar el poster, intenta nuevamente';
      }
    },
    close() {
      this.dialog = false;
    }
  }
};
</script>

<style scoped>
.poster-ad-modal .scope-hint {
  color: var(--pw-text-muted);
}
.poster-ad-modal .image-preview {
  border: 1px solid var(--pw-border);
  border-radius: var(--pw-radius-control);
}
</style>
